// Pacientes — lista filtrável/paginada + perfil (timeline, notas). API real.
// Cutover Fatia 6: /clinica/patients (+ /counts, :id, :id/notes). Adapter no
// front deriva apresentação (initials/avatarClass/statusLabel) e a timeline a
// partir do histórico de consultas do perfil (não há eventos ricos no backend).
import AXIOS from "@/services/axios";
import { initialsOf, avatarClassFor } from "./helpers";

const STATUS_LABELS = {
  ativo: "Ativo",
  sem_retorno: "Sem retorno",
  novo: "Novo",
  inativo: "Inativo"
};

// fmtDate das views faz split("-") → precisa de 'YYYY-MM-DD'.
function dateOnly(iso) {
  return iso ? String(iso).slice(0, 10) : null;
}

function adaptListItem(p, i) {
  return {
    id: p.id,
    name: p.name,
    initials: initialsOf(p.name),
    avatarClass: avatarClassFor(i),
    contact: p.phone_number,
    email: p.email,
    status: p.status,
    statusLabel: STATUS_LABELS[p.status] || p.status,
    // ISO completo (data + hora) — exibido via slotLabel(), não dateOnly().
    lastAppointment: p.last_appointment_at || null,
    lastDoctor: p.last_doctor // médico da última consulta (nome), vindo da API
  };
}

function adaptProfile(p) {
  const doc = p.responsible_doctor;
  return {
    id: p.id,
    name: p.name,
    email: p.email,
    contact: p.phone_number,
    cpf: p.cpf,
    birthday: p.birthday,
    initials: initialsOf(p.name),
    avatarClass: avatarClassFor(0),
    status: p.status,
    statusLabel: STATUS_LABELS[p.status] || p.status,
    // ISO completo (data + hora) — exibido via slotLabel(), não dateOnly().
    lastAppointment: p.last_appointment_at || null,
    daysSinceLast: p.days_since_last || 0,
    doctor: doc
      ? {
          name: doc.name,
          specialty: doc.specialty,
          crm: doc.crm,
          initials: initialsOf(doc.name)
        }
      : null,
    appointments: p.appointments || []
  };
}

// Sem eventos ricos no backend: a timeline é o histórico de consultas do perfil.
function timelineFromProfile(p) {
  return (p.appointments || []).map(a => ({
    id: a.id,
    type: "consulta",
    date: dateOnly(a.scheduled_at),
    description: `Consulta com ${a.doctor_name || "—"}`
  }));
}

function adaptNote(n) {
  return {
    id: n.id,
    author: n.author,
    date: dateOnly(n.created_at),
    text: n.text
  };
}

// dd/MM/yyyy a partir de um ISO (para exibição nas tabelas).
function brDate(iso) {
  const d = dateOnly(iso);
  return d
    ? d
        .split("-")
        .reverse()
        .join("/")
    : "—";
}

const PAY_STATUS = {
  recebido: "Recebido",
  pendente: "Pendente",
  estornado: "Estornado"
};

function adaptPayment(p) {
  return {
    id: p.id,
    appointmentId: p.appointment_id,
    date: brDate(p.date),
    reference: p.doctor_name ? `Consulta · ${p.doctor_name}` : "Consulta",
    method: p.method,
    status: PAY_STATUS[p.status] || p.status,
    valueCents: p.gross_cents,
    paid: p.status === "recebido"
  };
}

// type do backend (receita/atestado/exame) → kind do ícone da view (rx/at/ex).
const DOC_KINDS = { receita: "rx", atestado: "at", exame: "ex" };

function adaptDocument(d) {
  const dateLabel = d.date ? dateOnly(d.date) : null;
  return {
    id: `${d.type}-${d.id}`,
    kind: DOC_KINDS[d.type] || "rx",
    title: d.title,
    meta: [dateLabel, d.doctor_name].filter(Boolean).join(" · "),
    url: d.url
  };
}

// Busca global (search): sem status/last_appointment — paciente pode nunca
// ter sido atendido nesta clínica.
function adaptSearchItem(p) {
  return {
    id: p.id,
    name: p.name,
    contact: p.phone_number,
    email: p.email
  };
}

export class ClinicPatients {
  static list(params = {}) {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.status && params.status !== "todos")
      qs.set("status", params.status);
    if (params.page) qs.set("page", params.page);
    if (params.per) qs.set("per", params.per);
    const query = qs.toString() ? `?${qs.toString()}` : "";

    return AXIOS.get(`clinica/patients${query}`).then(res => ({
      data: {
        items: (res.data.items || []).map(adaptListItem),
        total: res.data.total,
        page: res.data.page,
        per: res.data.per,
        pages: res.data.pages
      }
    }));
  }

  // Qualquer paciente da plataforma (não só os já atendidos aqui) — usado no
  // campo de busca do "Novo agendamento".
  static search(q) {
    return AXIOS.get(`clinica/patients/search?q=${encodeURIComponent(q)}`).then(
      res => ({
        data: { items: (res.data.items || []).map(adaptSearchItem) }
      })
    );
  }

  /** CSV de todos os pacientes da lista (mesma busca e status, sem paginar). */
  static exportCsv(params = {}) {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.status && params.status !== "todos")
      qs.set("status", params.status);
    const query = qs.toString() ? `?${qs.toString()}` : "";
    return AXIOS.get(`clinica/patients/export${query}`, {
      responseType: "blob"
    });
  }

  static counts() {
    return AXIOS.get("clinica/patients/counts");
  }

  static get(id) {
    return AXIOS.get(`clinica/patients/${id}`).then(res => ({
      data: adaptProfile(res.data)
    }));
  }

  static timeline(id) {
    return AXIOS.get(`clinica/patients/${id}`).then(res => ({
      data: timelineFromProfile(res.data)
    }));
  }

  static notes(id) {
    return AXIOS.get(`clinica/patients/${id}/notes`).then(res => ({
      data: (res.data || []).map(adaptNote)
    }));
  }

  // Histórico completo de documentos do paciente (receitas, exames, atestados).
  static documents(id) {
    return AXIOS.get(`clinica/patients/${id}/documents`).then(res => ({
      data: (res.data || []).map(adaptDocument)
    }));
  }

  // Pagamentos do paciente nesta clínica.
  static payments(id) {
    return AXIOS.get(`clinica/patients/${id}/payments`).then(res => ({
      data: (res.data || []).map(adaptPayment)
    }));
  }

  static addNote(id, { text }) {
    return AXIOS.post(`clinica/patients/${id}/notes`, { text }).then(res => ({
      data: adaptNote(res.data)
    }));
  }
}

export default ClinicPatients;
